import React from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { sdk } from '@/lib/sdk'
import { useCommunity } from '@/lib/CommunityContext'
import { useTranslation } from 'react-i18next'
import { SectionFormFields, type SectionFormValues } from './SectionFormFields'
import { generateSlug, slugErrorFrom, slugProblem } from '@/lib/slug'
import { emptyAccessGrants, toAccessGrantInput } from '@/types/access'
import type { ApiErrorLike } from '@/lib/apiError'

/** A new section starts here, and comes back here when the dialog closes. */
const emptyValues = (): SectionFormValues => ({
  icon: 'folder',
  title: '',
  slug: '',
  description: '',
  visibility: 'members',
  access: emptyAccessGrants(),
  isHome: false,
  lockedDisplay: 'section_and_spaces'
})

interface CreateSectionDialogProps {
  onSectionCreated?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  trigger?: React.ReactNode
}

export const CreateSectionDialog = ({
  onSectionCreated,
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  trigger
}: CreateSectionDialogProps) => {
  const { t } = useTranslation(['common', 'navigation'])
  const { community } = useCommunity()
  const [internalOpen, setInternalOpen] = React.useState(false)

  const open = externalOpen !== undefined ? externalOpen : internalOpen
  const setOpen = externalOnOpenChange || setInternalOpen

  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [slugError, setSlugError] = React.useState<string | null>(null)

  const [values, setValues] = React.useState<SectionFormValues>(() => emptyValues())

  React.useEffect(() => {
    if (!open) {
      setValues(emptyValues())
      setError('')
      setSlugError(null)
      setIsLoading(false)
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!values.title.trim()) {
      setError(t('common:validation.titleRequired'))
      return
    }

    if (!community?.id) {
      setError(t('common:errors.noCommunity'))
      return
    }

    /* The final slug: what was typed, tidied, or the title's. Checked against
       the API's reserved words first, so the refusal comes before the request. */
    const slug = generateSlug(values.slug) || generateSlug(values.title)
    if (slugProblem(slug, 'section')) {
      setSlugError(t('common:sections.slugReserved', { slug }))
      return
    }

    setIsLoading(true)
    setError('')
    setSlugError(null)

    try {
      await sdk.sections.create({
        title: values.title.trim(),
        description: values.description.trim() || undefined,
        slug,
        icon: values.icon || 'home',
        order: 0,
        visibility: values.visibility,
        isHome: values.isHome,
        /* Only when it is private. Sending an empty set alongside another
           visibility would erase grants nobody asked to erase — and somebody
           who flips to Members and back expects to find their list intact. */
        access: values.visibility === 'private' ? toAccessGrantInput(values.access) : undefined,
        lockedDisplay: values.lockedDisplay
      })

      setOpen(false)
      onSectionCreated?.()
    } catch (caught) {
      const err = caught as ApiErrorLike
      const onSlug = slugErrorFrom(err)
      if (onSlug) setSlugError(onSlug)
      else setError(err?.response?.data?.message || t('common:errors.createFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent guard className="max-w-[540px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('navigation:sidebar.addSection')}</DialogTitle>
          <DialogDescription>
            {t('common:sections.createDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          <SectionFormFields
            values={values}
            onChange={setValues}
            disabled={isLoading}
            communityId={community?.id}
            autoSlug
            slugError={slugError}
          />

          <DialogFooter>
            <Button data-dialog-cancel type="button" variant="outline" onClick={() => setOpen(false)} disabled={isLoading}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" variant="secondary" disabled={isLoading}>
              {isLoading ? t('common:sections.creatingSection') : t('common:sections.createSection')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
